import DesignTokens from '@tableau/design-tokens';
import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import { DialogPositioningBehavior, DialogType, TabStyles } from '@tableau/widgets';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog, ReactActionDialogOptions } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ModalInstance, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { CheckConnectionCredentialsAction } from '~src/connectivity/services/CheckConnectionCredentialsAction';
import { Flows } from '~src/flow/resources/Flows';
import { RevertFlowResult } from '~src/flow/serverApi/requests/RevertFlowRequest';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { ICreateDraftFromPublishedConnectionVersionResult } from '~src/publishedConnection/serverApi/requests/CreateDraftFromPublishedConnectionVersionRequest';
import { IDoesActorHaveInflightRevisionForVconnResult } from '~src/publishedConnection/serverApi/requests/DoesActorHaveInflightRevisionForVConnRequest';
import { FlowRevision } from '~src/revisionHistoryFlow/serverApi/types/FlowRevision';
import { PublishedConnectionRevision } from '~src/revisionHistoryPublishedConnection/serverApi/types/PublishedConnectionRevision';
import { IWorkbookRevision } from '~src/revisionHistoryWorkbook/serverApi/types/IWorkbookRevision';
import { DatasourceContentType, FlowContentType, IContentType, PublishedConnectionContentType, WorkbookContentType } from '~src/spacesPlatform/models/ContentTypes';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { DownloadActions } from '~src/spacesPlatform/services/download/DownloadActions';
import { applyColor }from '~src/style/ColorService';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { IRevertWorkbookResult } from '~src/workbook/ServerApi/requests/RevertWorkbookRequest';

import {
  renderFooter,
  RevisionHistoryDialogBody,
  RevisionHistoryDialogBodyProps,
  RevisionHistoryDialogBodyState,
  VirtualConnectionWithInflightRevisionBody
} from '../components/RevisionHistoryDialogBody';


class RevisionHistoryService {

  private showContentRevisionHistory(content: INamedItemInfo, contentType: IContentType): Promise<IResult> {
    let dialogDescription = '';
    let action; // 10.0 dialog action for workbooks should be one-click revert. Otherwise it's download revision.
    let actionName: string;
    const elidedContentName = { NAME: L10n.elideIfLong(content.name) };
    switch (contentType.name) {
      case WorkbookContentType.name:
        action = this.getRevertWorkbookAction;
        actionName = Messages.revisionHistoryDialogSubmitRestore();
        dialogDescription = Messages.revisionHistoryDialogWorkbookDescription(elidedContentName);
        break;
      case DatasourceContentType.name:
        action = this.getDownloadRevisionAction;
        actionName = Messages.revisionHistoryDialogSubmitDownload();
        dialogDescription = Messages.revisionHistoryDialogDatasourceDescription(elidedContentName);
        break;
      case FlowContentType.name:
        action = this.getRevertFlowAction;
        actionName = Messages.revisionHistoryDialogSubmitRestore();
        dialogDescription = Messages.revisionHistoryDialogFlowDescription(elidedContentName);
        break;
      case PublishedConnectionContentType.name:
        action = this.getCreateDraftFromPublishedConnectionVersionAction;
        actionName = Messages.revisionHistoryDialogSubmitOpen();
        dialogDescription = Messages.revisionHistoryDialogPublishedConnectionDescription(elidedContentName);
        break;
      default:
        throw new Error(Messages.revisionHistoryUnsupportedContentType());
    }
    const revisionHistoryDialogOptions: ReactActionDialogOptions<RevisionHistoryDialogBodyProps, RevisionHistoryDialogBodyState, IResult> = {
      title: Messages.revisionHistoryDialogTitle(),
      initialBodyProps: { contentType, dialogDescription, contentItemId: content.id, contentLuid: content.luid },
      initialDialogState: { selectedItem: null, deletedItem: null },
      action: action.bind(this),
      bodyComponent: RevisionHistoryDialogBody,
      dismissDialogOnActionFailed: false,
      actionName: actionName,
      actionDisabled: this.isRestoreButtonDisabled,
      dialogWidth: FeatureFlags.enablePublishRevisionNotes ? TabDialogWidth.width700px : TabDialogWidth.width600px,
      isActionDelete: false
    };
    const instance = openReactActionDialog(revisionHistoryDialogOptions);
    return Promise.resolve(instance.result);
  }

  private getRevertWorkbookAction(props: RevisionHistoryDialogBodyProps,
    state: RevisionHistoryDialogBodyState,
    updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
    const revision: IWorkbookRevision = <IWorkbookRevision>state.selectedItem;
    return Workbooks.revertWorkbook(props.contentItemId, revision.versionNumber)
      .then((result: IRevertWorkbookResult): Promise<IRevertWorkbookResult> => {
        if (result.failedConnection) {
          const checkCredentialsDialogs = CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision);
          return Promise.resolve(checkCredentialsDialogs);
        } else {
          return Promise.resolve(result);
        }
      })
      .then((result: IRevertWorkbookResult) => {
        if (ResultHelper.hasErrors(result)) {
          updateState((prev) => ({ ...prev, errorMessage: Messages.revisionHistoryActionErrorForWorkbook() }));
          return Promise.reject(result);
        } else {
          ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessForWorkbook() });
          if (result.scheduledExtractCredentialsMayNotBeRestored) {
            ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessGoToConnections() });
          }
          return Promise.resolve(result);
        }
      });
  }

  private getDownloadRevisionAction(props: RevisionHistoryDialogBodyProps,
    state: RevisionHistoryDialogBodyState,
    updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
    DownloadActions.downloadContentRevision(state.selectedItem);
    return Promise.resolve({} as any);
  }

  private getRevertFlowAction(props: RevisionHistoryDialogBodyProps,
    state: RevisionHistoryDialogBodyState,
    updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
    const revision: FlowRevision = <FlowRevision>state.selectedItem;
    return Flows.revertFlow(props.contentItemId, revision.versionNumber)
      .then((result: RevertFlowResult): Promise<RevertFlowResult> => {
        if (result.failedConnection) {
          return Promise.resolve(CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision));
        } else {
          return Promise.resolve(result);
        }
      })
      .then((result: RevertFlowResult) => {
        if (ResultHelper.hasErrors(result)) {
          updateState((prev) => ({ ...prev, errorMessage: Messages.revisionHistoryActionErrorForFlow() }));
          return Promise.reject(result);
        } else {
          ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessForFlow() });
          return Promise.resolve(result);
        }
      });
  }

  private getCreateDraftFromPublishedConnectionVersionAction(props: RevisionHistoryDialogBodyProps,
    state: RevisionHistoryDialogBodyState,
    updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {

    const revision: PublishedConnectionRevision = <PublishedConnectionRevision>state.selectedItem;
    return this.doesUserHaveInflightRevision(props.contentItemId).then((result: IDoesActorHaveInflightRevisionForVconnResult) => {
      if (result) {
       return this.openVirtualConnectionWithInflightRevisionDialog().then((result: any)=>{
        return PublishedConnections.createDraftFromPublishedConnectionVersion(props.contentItemId, revision.versionNumber)
          .then((result: ICreateDraftFromPublishedConnectionVersionResult): Promise<ICreateDraftFromPublishedConnectionVersionResult> => {
            WindowLocationService.open(result.draftEditorUrl);
            return Promise.resolve(result);
          })
          .then((result: ICreateDraftFromPublishedConnectionVersionResult) => {
            if (ResultHelper.hasErrors(result)) {
              updateState((prev) => ({ ...prev, errorMessage: Messages.revisionHistoryActionErrorForPublishedConnection() }));
              return Promise.reject(result);
            } else {
              return Promise.resolve(result);
            }
          });
       });
      } else {
        return PublishedConnections.createDraftFromPublishedConnectionVersion(props.contentItemId, revision.versionNumber)
          .then((result: ICreateDraftFromPublishedConnectionVersionResult): Promise<ICreateDraftFromPublishedConnectionVersionResult> => {
            WindowLocationService.open(result.draftEditorUrl);
            return Promise.resolve(result);
          })
          .then((result: ICreateDraftFromPublishedConnectionVersionResult) => {
            if (ResultHelper.hasErrors(result)) {
              updateState((prev) => ({ ...prev, errorMessage: Messages.revisionHistoryActionErrorForPublishedConnection() }));
              return Promise.reject(result);
            } else {
              return Promise.resolve(result);
            }
          });
      }
    });
  }

  private doesUserHaveInflightRevision(id: string): Promise<IResult> {
    return PublishedConnections.doesUserHaveInflightRevision(id)
      .then((result: IDoesActorHaveInflightRevisionForVconnResult): Promise<IDoesActorHaveInflightRevisionForVconnResult> => {
        return Promise.resolve(result);
      })
      .then((result: IDoesActorHaveInflightRevisionForVconnResult) => {
        if (ResultHelper.hasErrors(result)) {
          return Promise.reject(result);
        } else {
          return Promise.resolve(result);
        }
      });
  }

  public showDataSourceRevisionHistory(datasource: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(datasource, DatasourceContentType);
  }

  public showWorkbookRevisionHistory(workbook: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(workbook, WorkbookContentType);
  }

  public showFlowRevisionHistory(flow: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(flow, FlowContentType);
  }

  public showPublishedConnectionRevisionHistory(publishedConnection: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(publishedConnection, PublishedConnectionContentType);
  }

  private isRestoreButtonDisabled(props: RevisionHistoryDialogBodyProps, state: RevisionHistoryDialogBodyState) {
    return state.selectedItem === null;
  }


  private openVirtualConnectionWithInflightRevisionDialog(): Promise<any> {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: () => modalInstance && modalInstance.close(),
    };

    const virtualConnectionWithInflightRevisionDialogStyle: React.CSSProperties = {
      color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),
    };

    modalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: Messages.openRevision(),
      dialogContent: VirtualConnectionWithInflightRevisionBody(),
      testId: 'vconn-ifr-dialog',
      showCloseButton: false,
      dialogContentStyle: virtualConnectionWithInflightRevisionDialogStyle,
      shouldNotSetDefaultWidth: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      shouldNotCloseOnGlassClick: true,
      dialogWidth: TabDialogWidth.width375px,
      footerContent: renderFooter({ onCancel: dialogProps.dismiss, onDiscardAndSubmit: dialogProps.close })
    });
    return modalInstance.result;
  }
}

export const RevisionHistoryServiceInstance = new RevisionHistoryService();
export type { RevisionHistoryService };
